import { AppError } from './AppError';
import { ValidationError } from './ValidationError';
import { InternalServerError } from './InternalServerError';
import { ValidationError as ClassValidatorError } from 'class-validator';

/**
 * HTTP status codes for different error types.
 */
export const ErrorStatusCodes = {
  ValidationError: 400,
  BadRequestError: 400,
  NotAuthenticatedError: 401,
  ForbiddenError: 403,
  NotFoundError: 404,
  ConflictError: 409,
  InternalServerError: 500,
  // Default status code for unknown error types
  default: 500
};

/**
 * Gets the HTTP status code for an error.
 * @param error The error to get the status code for.
 * @returns The HTTP status code.
 */
export function getErrorStatusCode(error: Error): number {
  if (error instanceof AppError) {
    // @ts-ignore
    return ErrorStatusCodes[error.constructor.name] || ErrorStatusCodes.default;
  }
  return ErrorStatusCodes.default;
}

/**
 * Converts an error to an AppError.
 * @param error The error to convert.
 * @returns An AppError.
 */
export function toAppError(error: Error | unknown): AppError {
  // If it's already an AppError, return it
  if (error instanceof AppError) {
    return error;
  }

  // If it's a validation error from class-validator, convert it to a ValidationError
  if (Array.isArray(error) && error.length > 0 && error[0] instanceof ClassValidatorError) {
    return ValidationError.fromValidationErrors(error as ClassValidatorError[]);
  }

  // If it's a standard Error, convert it to an InternalServerError
  if (error instanceof Error) {
    return InternalServerError.fromError(error);
  }

  // If it's something else, convert it to a string and create an InternalServerError
  return new InternalServerError(`Unknown error: ${String(error)}`);
}

/**
 * Safely executes a function and catches any errors, converting them to AppErrors.
 * @param fn The function to execute.
 * @returns A promise that resolves to the result of the function or rejects with an AppError.
 */
export async function tryCatch<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Determines if an error is operational (expected) or programming (unexpected).
 * @param error The error to check.
 * @returns True if the error is operational, false otherwise.
 */
export function isOperationalError(error: Error): boolean {
  if (error instanceof AppError) {
    return error.isOperational;
  }
  return false;
}

/**
 * Formats an error for logging.
 * @param error The error to format.
 * @returns A formatted error object for logging.
 */
export function formatErrorForLogging(error: Error): Record<string, any> {
  if (error instanceof AppError) {
    return {
      name: error.name,
      message: error.message,
      code: error.code,
      isOperational: error.isOperational,
      stack: error.stack,
      data: error.data
    };
  }
  
  return {
    name: error.name,
    message: error.message,
    stack: error.stack
  };
}